import {type RecordOf, Record, List} from 'immutable';

import {
  defaultTimedActionList,
  defaultContinuousMouseActionList,
  defaultContinuousScrollActionList,
  defaultContinuousActionGroups,
  defaultContinuousParameterGroupConsts,
} from '@packages/systems/ix2/designer/records/pojo';

import type {ActionItemsRecordType} from './animation-actions-immutable';

/**
 * ----------------------------------------------------------------------------
 * SHARED BETWEEN ACTION LISTS TYPES (actionItems)
 * ----------------------------------------------------------------------------
 */

type ActionItemGroups = {
  actionItems: ActionItemsRecordType;
};

export type IX2ActionItemGroupType = RecordOf<ActionItemGroups>;

/**
 * ----------------------------------------------------------------------------
 * TIMED ACTION LIST
 * ----------------------------------------------------------------------------
 */

export const TimedActionItemGroupRecord: Record.Factory<ActionItemGroups> =
  Record({actionItems: List()}, 'TimedActionItemGroupRecord');

type TimedActionList = typeof defaultTimedActionList & {
  actionItemGroups: List<IX2ActionItemGroupType>;
};

export type IX2TimedActionListType = RecordOf<TimedActionList>;

// @ts-expect-error - TS2322 - Type 'Factory<{ actionItemGroups: List<IX2ActionItemGroupType>; id: string; title: string; useFirstGroupAsInitialState: boolean; createdOn: number | null; }>' is not assignable to type 'Factory<TimedActionList>'.
export const TimedActionListRecord: Record.Factory<TimedActionList> = Record(
  {
    ...defaultTimedActionList,
    actionItemGroups: List(),
  },
  'TimedActionListRecord'
);

/**
 * ----------------------------------------------------------------------------
 * CONTINUOUS ACTION LIST
 * ----------------------------------------------------------------------------
 */

type ContinuousActionGroup = {
  keyframe: (typeof defaultContinuousActionGroups)['keyframe'];
  actionItems: ActionItemsRecordType;
};

export type IX2ContinuousActionGroupType = RecordOf<ContinuousActionGroup>;

export const ContinuousActionGroupRecord: Record.Factory<ContinuousActionGroup> =
  Record(
    {...defaultContinuousActionGroups, actionItems: List()},
    'ContinuousActionGroupRecord'
  );

type ContinuousParameterGroupsMouseX =
  typeof defaultContinuousParameterGroupConsts.MOUSE_X & {
    continuousActionGroups: List<IX2ContinuousActionGroupType>;
  };
type ContinuousParameterGroupsMouseY =
  typeof defaultContinuousParameterGroupConsts.MOUSE_Y & {
    continuousActionGroups: List<IX2ContinuousActionGroupType>;
  };
type ContinuousParameterGroupsScroll =
  typeof defaultContinuousParameterGroupConsts.SCROLL_PROGRESS & {
    continuousActionGroups: List<IX2ContinuousActionGroupType>;
  };

export type IX2ContinuousParameterGroupMouseX =
  RecordOf<ContinuousParameterGroupsMouseX>;
export type IX2ContinuousParameterGroupMouseY =
  RecordOf<ContinuousParameterGroupsMouseY>;
export type IX2ContinuousParameterGroupScroll =
  RecordOf<ContinuousParameterGroupsScroll>;
export type IX2ContinuousParameterGroup =
  | IX2ContinuousParameterGroupMouseX
  | IX2ContinuousParameterGroupMouseY
  | IX2ContinuousParameterGroupScroll;

export const ContinuousParameterGroupRecordConsts: {
  MOUSE_X: Record.Factory<ContinuousParameterGroupsMouseX>;
  MOUSE_Y: Record.Factory<ContinuousParameterGroupsMouseY>;
  SCROLL_PROGRESS: Record.Factory<ContinuousParameterGroupsScroll>;
} = {
  // @ts-expect-error - TS2322 - Type 'Factory<{ continuousActionGroups: List<IX2ContinuousActionGroupType>; id: string | null; type: "SCROLL_PROGRESS" | "MOUSE_X" | "MOUSE_Y"; parameterLabel: string; }>' is not assignable to type 'Factory<ContinuousParameterGroupsMouseX>'.
  MOUSE_X: Record(
    {
      ...defaultContinuousParameterGroupConsts.MOUSE_X,
      continuousActionGroups: List(),
    },
    'ContinuousParameterGroupMouseXRecord'
  ),
  // @ts-expect-error - TS2322 - Type 'Factory<{ continuousActionGroups: List<IX2ContinuousActionGroupType>; id: string | null; type: "SCROLL_PROGRESS" | "MOUSE_X" | "MOUSE_Y"; parameterLabel: string; }>' is not assignable to type 'Factory<ContinuousParameterGroupsMouseY>'.
  MOUSE_Y: Record(
    {
      ...defaultContinuousParameterGroupConsts.MOUSE_Y,
      continuousActionGroups: List(),
    },
    'ContinuousParameterGroupMouseYRecord'
  ),
  // @ts-expect-error - TS2322 - Type 'Factory<{ continuousActionGroups: List<IX2ContinuousActionGroupType>; id: string | null; type: "SCROLL_PROGRESS" | "MOUSE_X" | "MOUSE_Y"; parameterLabel: string; }>' is not assignable to type 'Factory<ContinuousParameterGroupsScroll>'.
  SCROLL_PROGRESS: Record(
    {
      ...defaultContinuousParameterGroupConsts.SCROLL_PROGRESS,
      continuousActionGroups: List(),
    },
    'ContinuousParameterGroupScrollProgressRecord'
  ),
};

type ContinuousMouseActionList = typeof defaultContinuousMouseActionList & {
  continuousParameterGroups: List<IX2ContinuousParameterGroup>;
};

type ContinuousScrollActionList = typeof defaultContinuousScrollActionList & {
  continuousParameterGroups: List<IX2ContinuousParameterGroup>;
};

export type IX2ContinuousMouseActionList = RecordOf<ContinuousMouseActionList>;
export type IX2ContinuousScrollActionList =
  RecordOf<ContinuousScrollActionList>;

// @ts-expect-error - TS2322 - Type 'Factory<{ continuousParameterGroups: List<Record<ContinuousParameterGroupsMouseX> & Readonly<ContinuousParameterGroupsMouseX>>; id: string; title: string; createdOn: number | null; }>' is not assignable to type 'Factory<ContinuousMouseActionList>'.
export const ContinuousMouseActionListRecord: Record.Factory<ContinuousMouseActionList> =
  Record(
    {
      ...defaultContinuousMouseActionList,
      continuousParameterGroups: List([
        ContinuousParameterGroupRecordConsts.MOUSE_X(),
        ContinuousParameterGroupRecordConsts.MOUSE_Y(),
      ]),
    },
    'ContinuousMouseActionListRecord'
  );

// @ts-expect-error - TS2322 - Type 'Factory<{ continuousParameterGroups: List<Record<ContinuousParameterGroupsScroll> & Readonly<ContinuousParameterGroupsScroll>>; id: string; title: string; createdOn: number | null; }>' is not assignable to type 'Factory<ContinuousScrollActionList>'.
export const ContinuousScrollActionListRecord: Record.Factory<ContinuousScrollActionList> =
  Record(
    {
      ...defaultContinuousScrollActionList,
      continuousParameterGroups: List([
        ContinuousParameterGroupRecordConsts.SCROLL_PROGRESS(),
      ]),
    },
    'ContinuousScrollActionListRecord'
  );

export type ActionListRecordType =
  | IX2TimedActionListType
  | IX2ContinuousMouseActionList
  | IX2ContinuousScrollActionList;

export type ActionListsRecordType = List<ActionListRecordType>;
